import { ThemeProvider, css } from "@emotion/react";
import styled from "@emotion/styled";
import { differenceInDays } from "date-fns";
import { omit, uniq } from "lodash";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Add, Search } from "@livechat/design-system-icons";
import {
  Button,
  Text as DSText,
  DesignToken,
  Heading,
  Icon,
  SpacingToken,
} from "@livechat/design-system-react-components";

import Redirect from "components/Redirect";
import { OnboardingDrawer } from "components/drawer/onboarding-drawer/OnboardingDrawer";
import { IFilterUpdate } from "components/filters/interfaces";
import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { OnboardingBar } from "components/onboarding/OnboardingBar";
import { SideNavigation } from "components/side-navigation";
import { newFeaturesCampaigns } from "constants/campaigns";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction } from "constants/tickets/batch-action";
import { getTicketNavigationItems } from "constants/tickets/navigation-items";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isInIframe } from "helpers/app";
import { setDocumentTitle } from "helpers/routing";
import { getActiveItems } from "helpers/side-navigation";
import { inTrial } from "helpers/subscription";
import { getDaysLeft } from "helpers/subscription-plans";
import { getRouteByPathname, getRouteTitle } from "helpers/tickets";
import { IKeyValue } from "helpers/url";
import { useBreakpoints } from "hooks/use-breakpoints";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { useRedirectToTextApp } from "hooks/use-redirect-to-text-app";
import { ISideNavigationItem } from "interfaces/sideNavigationItem";
import { selectAgentsAlphabetically, selectIsAdmin, selectLoggedInAgent } from "store/entities/agents/selectors";
import { selectApplicationSettings } from "store/entities/application-settings/selectors";
import {
  selectCanCreateCustomViews,
  selectCanSeeCustomViews,
  selectCustomViewsWithStatusAlphabetically,
} from "store/entities/custom-views/selectors";
import { selectAreMailboxesSetUp } from "store/entities/email-domains";
import { selectLicense, selectPurgeSpamAfterDays, selectPurgeTrashAfterDays } from "store/entities/license";
import { selectCurrentSubscription } from "store/entities/subscriptions";
import { selectAgentTagsAlphabetically, selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically, selectTeamsPerPermission } from "store/entities/teams";
import { selectCanAccessSettings } from "store/features/app";
import { getTeamsUsageAllowed, getTicketsFromOtherTeamsAllowed } from "store/features/session/selectors";
import { UrlQueryParamsFeatureActions } from "store/features/url-query-params/actions";
import { UrlQueryParamsView } from "store/features/url-query-params/interfaces";
import { TicketsViewActions } from "store/views/tickets/actions";
import { ITicketsViewBatchUpdatePayloadData } from "store/views/tickets/interfaces";
import {
  getAnyDataFetched,
  getCurrentPage,
  getDataFetchedForRoute,
  getFilters,
  getFiltersOrder,
  getFocusedColumn,
  getIsBatchUpdating,
  getIsFetching,
  getNavigationItemCount,
  getRoute,
  getSearchQuery,
  getSelectedTicketIds,
  getTickets,
  getTotalPages,
  getTotalResults,
  selectActiveCustomView,
  selectActiveCustomViewId,
  selectCanCreateTickets,
} from "store/views/tickets/selectors";
import { buildTicketsViewQueryString, parseTicketsViewURLParams } from "store/views/tickets/url-query-params";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";

import { LiveChatMenu } from "./LiveChatMenu";
import { TicketsList } from "./TicketsList";
import { TicketsSearchBar } from "./TicketsSearchBar";
import { DISPLAYING_ONBOARDING_DRAWER_TRIAL_DAYS_LIMIT } from "./constants";
import SpamListModal from "./ticket/SpamListModal";

const TicketsSidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ButtonPadder = styled.div`
  padding-right: 8px;
`;

export const TicketsGrid: React.FC = () => {
  const { isFetching: textAppRedirectFetching, isRedirecting } = useRedirectToTextApp("/inbox/tickets");

  const location = useLocation();
  const { isMobile } = useBreakpoints();
  const dispatch = useDispatch();
  const componentMounted = useRef(false);

  const tickets = useSelector(getTickets);
  const customViews = useSelector(selectCustomViewsWithStatusAlphabetically);
  const isAdmin = useSelector(selectIsAdmin);
  const anyDataFetched = useSelector(getAnyDataFetched);
  const currentPage = useSelector(getCurrentPage);
  const focusedColumn = useSelector(getFocusedColumn);
  const totalPages = useSelector(getTotalPages);
  const isFetching = useSelector(getIsFetching);
  const filters = useSelector(getFilters);
  const filtersOrder = useSelector(getFiltersOrder);
  const selectedIds = useSelector(getSelectedTicketIds);
  const agents = useSelector(selectAgentsAlphabetically);
  const currentAgent = useSelector(selectLoggedInAgent);
  const ticketsFromOtherTeamsAllowed = useSelector(getTicketsFromOtherTeamsAllowed);
  const areMailboxesSetUp = useSelector(selectAreMailboxesSetUp);
  const canAccessSettings = useSelector(selectCanAccessSettings);
  const teams = useSelector(selectTeamsPerPermission);
  const allTeams = useSelector(selectTeamsAlphabetically);
  const tags = useSelector(selectAgentTagsAlphabetically);
  const allTags = useSelector(selectTagsAlphabetically);
  const dataFetchedForRoute = useSelector(getDataFetchedForRoute);
  const teamsUsageAllowed = useSelector(getTeamsUsageAllowed);
  const searchQuery = useSelector(getSearchQuery);
  const totalResults = useSelector(getTotalResults);
  const isBatchUpdating = useSelector(getIsBatchUpdating);
  const navigationItemCount = useSelector(getNavigationItemCount);
  const activeCustomViewId = useSelector(selectActiveCustomViewId);
  const activeCustomView = useSelector(selectActiveCustomView);
  const route = useSelector(getRoute);
  const canCreateTickets = useSelector(selectCanCreateTickets);
  const canSeeCustomViews = useSelector(selectCanSeeCustomViews);
  const canCreateCustomViews = useSelector(selectCanCreateCustomViews);
  const purgeTrashAfterDays = useSelector(selectPurgeTrashAfterDays);
  const purgeSpamAfterDays = useSelector(selectPurgeSpamAfterDays);
  const subscription = useSelector(selectCurrentSubscription);
  const license = useSelector(selectLicense);
  const { ticketSourceMapping } = useSelector(selectApplicationSettings);

  const { createdAt } = license;
  const isTrial = inTrial(subscription);
  const trialDaysLeft = getDaysLeft(subscription);
  const appInFrame = isInIframe();
  const shouldSeeOnboardingDrawer =
    isAdmin &&
    isTrial &&
    trialDaysLeft <= DISPLAYING_ONBOARDING_DRAWER_TRIAL_DAYS_LIMIT &&
    differenceInDays(Date.now(), createdAt) <= DISPLAYING_ONBOARDING_DRAWER_TRIAL_DAYS_LIMIT &&
    !isMobile;

  const {
    isNewForAgent: isNewForAgentUndeliveredTickets,
    featureFlagValue: featureFlagValueUndeliveredTickets,
    setAgentFlagCallback: setHasEnteredUndeliveredTickets,
  } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasEnteredUndeliveredTickets.flag,
    newFeaturesCampaigns.hasEnteredUndeliveredTickets.campaignStartDate
  );
  const shouldShowNewBadgeOnUndeliveredTickets =
    isNewForAgentUndeliveredTickets && !featureFlagValueUndeliveredTickets && !isTrial;

  const handleMount = (route: TicketsRoutes): void => {
    dispatch(TicketsViewActions.setInitialRoute({ route }));
    dispatch(
      UrlQueryParamsFeatureActions.registerView({
        view: UrlQueryParamsView.Tickets,
        setQueryParamsAction: TicketsViewActions.setQueryParams,
        queryStringSerializer: buildTicketsViewQueryString,
        queryStringParser: (queryParams: IKeyValue<string>, pathname: string) =>
          parseTicketsViewURLParams(queryParams, ticketSourceMapping, pathname) as IKeyValue<string>,
      })
    );
  };

  const handleUnmount = (): void => {
    dispatch(UrlQueryParamsFeatureActions.unregisterView({ view: UrlQueryParamsView.Tickets }));
  };

  const handlePageChange = (page: number): void => {
    dispatch(TicketsViewActions.setPage(page));
  };

  const handleFilterUpdate = (filterUpdate: IFilterUpdate): void => {
    dispatch(TicketsViewActions.updateFilter(filterUpdate));
  };

  const handleSelectedIdsUpdate = (ticketIds: string[]): void => {
    dispatch(TicketsViewActions.selectTicketIds(ticketIds));
  };

  const handleBatchUpdate = (
    ticketIds: string[],
    action: TicketsBatchAction,
    data?: ITicketsViewBatchUpdatePayloadData
  ): void => {
    dispatch(TicketsViewActions.updateBatch({ ticketIds, batchAction: action, data }));
  };

  const handleSearchQueryChange = (query: string): void => {
    dispatch(TicketsViewActions.setSearchQuery(query));
  };

  const handleSearchQueryClear = (): void => {
    dispatch(TicketsViewActions.clearSearchQuery());
  };

  const handleAllTicketsLinkClick = (route: TicketsRoutes): void => {
    dispatch(TicketsViewActions.showTicketsFromAllTeams({ route }));
  };

  const handleBackToList = (): void => {
    dispatch(TicketsViewActions.setFocusedColumn(0));
  };

  const handleBackToTickets = (): void => {
    dispatch(TicketsViewActions.setFocusedColumn(1));
  };

  const handleSetFocusedColumn = (focusedColumn: number): void => {
    dispatch(TicketsViewActions.setFocusedColumn(focusedColumn));
  };

  const handleSelectCustomView = (id: string): void => {
    dispatch(TicketsViewActions.selectCustomView({ id }));
  };

  const handleSelectStandardView = (route: TicketsRoutes): void => {
    dispatch(TicketsViewActions.selectStandardView({ route }));
  };

  useEffect(() => {
    if (componentMounted.current) {
      return;
    }
    const currentRoute = getRouteByPathname(location.pathname) as TicketsRoutes;

    handleMount(currentRoute);
    setDocumentTitle(getRouteTitle(currentRoute));

    componentMounted.current = true;
    return (): void => {
      componentMounted.current = false;
      handleUnmount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentRoute = getRouteByPathname(location.pathname) as TicketsRoutes;

    if (
      currentRoute === TicketsRoutes.Undelivered &&
      !featureFlagValueUndeliveredTickets &&
      isNewForAgentUndeliveredTickets
    ) {
      setHasEnteredUndeliveredTickets();
    }
  }, [
    featureFlagValueUndeliveredTickets,
    isNewForAgentUndeliveredTickets,
    setHasEnteredUndeliveredTickets,
    location.pathname,
  ]);

  useEffect(() => {
    setDocumentTitle(activeCustomView ? activeCustomView.name : getRouteTitle(route));
  }, [route, activeCustomView]);

  const getSearchBarReactKey = (): string | undefined => {
    if (componentMounted.current && route === TicketsRoutes.Search) {
      return TicketsRoutes.Search;
    }
    return searchQuery;
  };

  const withNumbers = (items: ISideNavigationItem[]): ISideNavigationItem[] =>
    items.map(item => {
      if (item.id && navigationItemCount[item.id as TicketsRoutes] != null) {
        return {
          ...item,
          number: navigationItemCount[item.id as TicketsRoutes],
        };
      }
      return omit(item, ["number"]);
    });

  const activeMenuItem = useMemo(() => {
    const navigationItems = getTicketNavigationItems(
      customViews,
      canSeeCustomViews,
      canCreateCustomViews,
      navigationItemCount,
      true, // hasUndeliveredTickets placeholder
      shouldShowNewBadgeOnUndeliveredTickets
    );

    if (activeCustomViewId) {
      // assume it exists
      return navigationItems.find(item => item.id === activeCustomViewId) as ISideNavigationItem;
    }
    return getActiveItems(navigationItems, route).activeParentItem;
  }, [
    customViews,
    canSeeCustomViews,
    canCreateCustomViews,
    navigationItemCount,
    activeCustomViewId,
    route,
    shouldShowNewBadgeOnUndeliveredTickets,
  ]);

  const navigationItems = useMemo(() => {
    return withNumbers(
      getTicketNavigationItems(
        customViews,
        canSeeCustomViews,
        canCreateCustomViews,
        navigationItemCount,
        true, // hasUndeliveredTickets placeholder
        shouldShowNewBadgeOnUndeliveredTickets
      )
    );
  }, [
    customViews,
    canSeeCustomViews,
    canCreateCustomViews,
    navigationItemCount,
    shouldShowNewBadgeOnUndeliveredTickets,
  ]);

  const getTitle = (): React.ReactNode => {
    const subtitleMap = {
      [TicketsRoutes.Spam]: `Tickets moved to Spam will be automatically deleted after ${purgeSpamAfterDays} days.`,
      [TicketsRoutes.Trash]: `Tickets moved to Trash will be automatically deleted after ${purgeTrashAfterDays} days.`,
      [TicketsRoutes.Undelivered]: "All tickets that failed to reach the requester within the last 7 days.",
    };

    const title = route === TicketsRoutes.Search ? `Search results for ${searchQuery}` : (activeMenuItem?.name ?? "");

    return <HeaderWithSub title={title} subtitle={subtitleMap[route as keyof typeof subtitleMap]} />;
  };

  const [createTicket, setCreateTicket] = useState(false);
  const [spamModal, setSpamModal] = useState<null | ["spam" | "unspam", string[]]>(null);

  const handleSpamModalRequest = (modalType: "spam" | "unspam", selectedIds: string[]): void => {
    const requesterEmails = uniq(tickets.filter(t => selectedIds.includes(t.id)).map(t => t.requester.email));
    setSpamModal([modalType, requesterEmails]);
  };

  const handleNavigateToCreateTicket = (): void => {
    trackEngagement("Tickets: New manual ticket clicked", EventPlace.Tickets);
    setCreateTicket(true);
  };

  const handleNavigationItemClick = (routeOrCustomViewId: string): void => {
    handleSetFocusedColumn(1);
    const selectedCustomView = customViews.find(item => item.id === routeOrCustomViewId);
    if (selectedCustomView) {
      handleSelectCustomView(selectedCustomView.id);
    } else {
      handleSelectStandardView(routeOrCustomViewId as TicketsRoutes);
    }
  };

  if (createTicket) {
    return <Redirect to="/tickets/add" push />;
  }

  if (textAppRedirectFetching || isRedirecting) {
    return null;
  }

  return (
    <ThemeProvider theme={legacyTheme}>
      <Grid relativeWidths={[1, 10]} minWidths={[280, 0]} focusedColumn={focusedColumn}>
        <ViewStack>
          <ThemeProvider theme={legacyTheme}>
            <ViewContainer
              title="Tickets"
              rightNodes={
                <ButtonPadder>
                  <Button
                    kind="primary"
                    size="compact"
                    onClick={handleNavigateToCreateTicket}
                    disabled={!canCreateTickets}
                    icon={<Icon source={Add} />}
                    data-testid="new-ticket-btn"
                  >
                    New ticket
                  </Button>
                </ButtonPadder>
              }
              backButton={isMobile ? <BackButton onClick={handleBackToTickets} /> : undefined}
              stackShowBackButton={isMobile}
              contentCss={css`
                z-index: auto;
              `}
            >
              <TicketsSidebarWrapper>
                <TicketsSearchBar
                  key={getSearchBarReactKey()}
                  route={route}
                  searchQuery={searchQuery}
                  ticketsAreFetching={isFetching}
                  onSearchQueryChange={handleSearchQueryChange}
                  onSearchQueryClear={handleSearchQueryClear}
                />
                <Block flex={1} maxHeight="100%" overflow="auto">
                  <SideNavigation
                    activeItemId={activeCustomViewId || route}
                    items={navigationItems}
                    onItemClick={handleNavigationItemClick}
                  />
                </Block>
                {appInFrame && <LiveChatMenu />}
              </TicketsSidebarWrapper>
            </ViewContainer>
          </ThemeProvider>
        </ViewStack>
        <ViewStack>
          <ViewContainer
            title={getTitle()}
            backButton={isMobile ? <BackButton iconSource={Search} onClick={handleBackToList} /> : undefined}
            placement={isMobile ? "main-content" : "sidebar"}
            rightNodes={shouldSeeOnboardingDrawer && <OnboardingDrawer trigger={OnboardingBar} />}
          >
            <TicketsList
              canCreateTickets={canCreateTickets}
              data={tickets}
              dataFetchedForRoute={dataFetchedForRoute}
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={totalResults}
              searchQuery={searchQuery}
              currentAgent={currentAgent}
              agents={agents}
              ticketsFromOtherTeamsAllowed={ticketsFromOtherTeamsAllowed}
              teams={teams}
              allTeams={allTeams}
              teamsUsageAllowed={teamsUsageAllowed}
              isBatchUpdating={isBatchUpdating}
              route={route}
              anyDataFetched={anyDataFetched}
              areMailboxesSetUp={areMailboxesSetUp}
              canAccessSettings={canAccessSettings}
              selectedIds={selectedIds}
              onBatchUpdate={handleBatchUpdate}
              onSelectedIdsUpdate={handleSelectedIdsUpdate}
              tags={tags}
              allTags={allTags}
              filters={filters}
              onSpamModalRequest={handleSpamModalRequest}
              filtersOrder={filtersOrder}
              isFetching={isFetching ?? false}
              onFilterUpdate={handleFilterUpdate}
              onPageChange={handlePageChange}
              onSearchClear={handleSearchQueryClear}
              onAllTicketsLinkClick={handleAllTicketsLinkClick}
            />
          </ViewContainer>
        </ViewStack>
      </Grid>
      {spamModal !== null && (
        <SpamListModal modalType={spamModal[0]} requesterEmails={spamModal[1]} onClose={() => setSpamModal(null)} />
      )}
    </ThemeProvider>
  );
};

const HeaderWithSub = ({ title, subtitle }: { title: string; subtitle?: string }): React.ReactNode => {
  const { isMobile } = useBreakpoints();

  const showSubtitle = subtitle && !isMobile;

  return (
    <Flex column css={{ height: "100%", justifyContent: showSubtitle ? "flex-start" : "center" }}>
      <Heading size="sm" css={[{ marginTop: showSubtitle ? withVar(SpacingToken.Spacing1) : 0 }, textEllipsisStyles]}>
        {title}
      </Heading>
      {showSubtitle && (
        <DSText size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)} css={textEllipsisStyles}>
          {subtitle}
        </DSText>
      )}
    </Flex>
  );
};
